/**
 * RateChangeDialog Component
 * Non-dismissable blocking dialog shown when a draft report's powers have
 * outdated commission rates. User must click "Continue" to recalculate
 * NET/BUF values before working with the report.
 */

import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { recalculateReport } from '../../../../services/oathPowerReportService';
import { OathButton } from '../../../shared/OathUI';
import { formatCurrencyWithSymbol } from '../utils/formatters';

const RateChangeDialog = ({ isOpen, rateChangeData, reportId, onRecalculated }) => {
  const [recalculating, setRecalculating] = useState(false);

  const handleContinue = async () => {
    setRecalculating(true);
    try {
      const result = await recalculateReport(reportId);
      if (result.success) {
        toast.success('Commission rates updated successfully.');
        onRecalculated(result.data);
      } else {
        toast.error('Failed to recalculate: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to recalculate rates: ' + error.message);
      console.error('Recalculate error:', error);
    } finally {
      setRecalculating(false);
    }
  };

  if (!isOpen || !rateChangeData) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop - no onClick to prevent dismissal */}
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 flex-shrink-0" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Commission Rates Updated
            </h2>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Your agency's commission rates have changed since powers were added to this report.{' '}
              <strong>{rateChangeData.affectedPowerCount}</strong>{' '}
              power{rateChangeData.affectedPowerCount !== 1 ? 's' : ''} will be updated.
            </p>

            {/* Summary Table */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-4 py-2.5 font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900">
                      NET Due
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">
                      {formatCurrencyWithSymbol(rateChangeData.oldTotalNet)}
                    </td>
                    <td className="px-2 py-2.5 text-center text-gray-400">
                      →
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-primary dark:text-primary-light">
                      {formatCurrencyWithSymbol(rateChangeData.newTotalNet)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900">
                      BUF
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">
                      {formatCurrencyWithSymbol(rateChangeData.oldTotalBuf)}
                    </td>
                    <td className="px-2 py-2.5 text-center text-gray-400">
                      →
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-primary dark:text-primary-light">
                      {formatCurrencyWithSymbol(rateChangeData.newTotalBuf)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400">
              Click Continue to update the report with the new rates. This cannot be skipped.
            </p>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
            <OathButton
              variant="primary"
              onClick={handleContinue}
              disabled={recalculating}
              loading={recalculating}
            >
              {recalculating ? 'Updating...' : 'Continue'}
            </OathButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateChangeDialog;
